import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Table, 
  Input, 
  Button, 
  message,
  Typography,
  Avatar,
  Tag
} from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined } from '@ant-design/icons';
import PageLayout from '../../../components/layout/PageLayout';
import masterService from '../../../services/masterService';
import { useUser } from '../../../context/UserContext';

const { Title, Text } = Typography;
const { Search } = Input;

const MobileAppStoreList = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [mobileAppStores, setMobileAppStores] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} mobile app stores`
  });
  
  // Filters
  const [filters, setFilters] = useState({
    searchText: ''
  });

  const getMobileAppStores = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      
      const requestParams = {
        perPage: pagination.pageSize,
        currentPage: pagination.current,
        searchText: filters.searchText,
        ...params
      };
      
      console.log('Fetching mobile app stores with params:', requestParams);
      const response = await masterService.getMobileAppStores(requestParams);
      console.log('Mobile App Stores API Response:', response);
      
      if (response.data && response.data.status) {
        // Handle different possible response structures
        const mobileAppStoresData = response.data.data?.data || response.data.data || [];
        const paginationData = response.data.data?.pagination || response.data.pagination || {};
        
        console.log('Mobile App Stores data:', mobileAppStoresData);
        console.log('Pagination data:', paginationData);
        
        // Ensure mobileAppStoresData is an array
        if (Array.isArray(mobileAppStoresData)) {
          setMobileAppStores(mobileAppStoresData);
        } else {
          console.error('Mobile App Stores data is not an array:', mobileAppStoresData);
          setMobileAppStores([]);
        }
        
        setPagination(prev => ({
          ...prev,
          ...paginationData,
          current: paginationData.currentPage || 1
        }));
      } else {
        message.error('Failed to fetch mobile app stores');
        setMobileAppStores([]);
      }
    } catch (error) {
      console.error('Error fetching mobile app stores:', error);
      message.error('Failed to fetch mobile app stores');
      setMobileAppStores([]);
    } finally {
      setLoading(false);
    }
  }, [filters.searchText]);

  // Load mobile app stores data
  useEffect(() => {
    getMobileAppStores();
  }, [getMobileAppStores]);

  // Handle search
  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, searchText: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
    getMobileAppStores({
      searchText: value,
      perPage: pagination.pageSize,
      currentPage: 1
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;
    
    setPagination(prev => ({
      ...prev,
      current,
      pageSize
    }));
    
    getMobileAppStores({
      searchText: filters.searchText,
      perPage: pageSize,
      currentPage: current
    });
  };

  // Handle add mobile app store
  const handleAddMobileAppStore = () => {
    navigate('/master/addmobileappstore');
  };

  // Handle edit mobile app store
  const handleEditMobileAppStore = (storeId) => {
    navigate(`/master/editmobileappstore/${storeId}`);
  };

  // Table columns
  const columns = [
    {
      title: 'Store Name',
      dataIndex: 'store_name',
      key: 'store_name',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.store_name.localeCompare(b.store_name),
      render: (text) => (
        <Text strong style={{ fontSize: '14px' }}>
          {text}
        </Text>
      )
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 120,
      render: (status) => (
        <Tag color={status === 'Y' ? 'green' : 'red'}>
          {status === 'Y' ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditMobileAppStore(record.store_id)}
          size="small"
        >
          Edit
        </Button>
      )
    }
  ];

  // Header content with user info
  const headerContent = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Avatar 
          src={user?.avatar} 
          size={64} 
          style={{ marginRight: '16px' }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div>
          <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
            {user?.name || 'Admin'}
          </Title>
          <Text type="secondary">Manage Mobile App Stores</Text>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Mobile App Store List"
      desc="Manage mobile app stores and their status"
      avatar={user?.avatar}
      headerContent={headerContent}
    >
      <Card>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <Title level={4} style={{ margin: 0 }}>
              Mobile App Stores ({pagination.total})
            </Title>
            
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddMobileAppStore}
              size="large"
            >
              Add Mobile App Store
            </Button>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '16px', 
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <Search
              placeholder="Search mobile app stores..."
              allowClear
              onSearch={handleSearch}
              style={{ width: '50%', minWidth: '200px' }}
              enterButton={<SearchOutlined />}
            />
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={mobileAppStores}
          rowKey="store_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          size="middle"
          locale={{ 
            emptyText: 'No mobile app stores found',
            filterConfirm: 'OK',
            filterReset: 'Reset',
            filterEmptyText: 'No filters'
          }}
        />
      </Card>
    </PageLayout>
  );
};

export default MobileAppStoreList;